"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _classnames = _interopRequireDefault(require("classnames"));
var _label = _interopRequireWildcard(require("@ux/label"));
var _text = _interopRequireDefault(require("@ux/text"));
var _useControllableState = _interopRequireDefault(require("@ux/component-utilities/lib/use-controllable-state"));
const _excluded = ["className", "defaultValue", "disabled", "id", "invalid", "label", "multiline", "onBlur", "onChange", "onFocus", "placeholder", "resize", "required", "size", "suggestion", "type", "value", "visualSize"];
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && Object.prototype.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
const inputTypes = ['text', 'email', 'password', 'url', 'tel', 'number', 'search'];
const textSize = {
  md: 0,
  sm: -1
};
const UX_TEXT_ENTRY_CLASSNAME = 'ux-text-entry';

/**
 * Create props to be added to the shell of the component
 *
 * @param {Object} props - Props from the TextEntry component below
 * @param {String|React.Element} [props.label] - Label for the component
 * @param {Boolean} [props.multiline] - Renders input as textarea
 * @param {Boolean|String} [props.resize] - Allows a multiline input to be resized
 * @param {String} [props.size] - Sets the size of the input
 * @param {String|Number} [props.value] - Value for the input
 * @returns {Object} - Props to be applied to the shell
 */
function getShellProps(props) {
  const {
    multiline,
    label,
    size,
    resize
  } = props;
  const boolResize = resize ? 'both' : 'none';
  const style = {};
  if (multiline) {
    // If multiline, apply size in vertical direction with `lh`
    // Include logic for string/boolean resize
    style['grid-template-rows'] = `${size}lh`;
    style.resize = typeof resize === 'string' ? resize : boolResize;
  } else if (!label) {
    // If no label and not multiline, apply size in horizontal direction with `ch`
    style['grid-template-columns'] = `${size}ch`;
  }
  return {
    style
  };
}

/**
 * Text entry functional component
 * @param {Object} props - Information for the props can be found in the PropTypes below and in TextEntry propTypes
 * @param {String} [props.className] - Optional classname
 * @param {String} [props.defaultValue] - Default value for the input
 * @param {Boolean} [props.disabled] - Sets component as disabled
 * @param {String} props.id - Sets the id of the input
 * @param {Boolean} [props.invalid] - Sets component as invalid
 * @param {String|React.Element} [props.label] - Label for the component
 * @param {Boolean} [props.multiline] - Renders input as textarea
 * @param {String} [props.placeholder] - Sets placeholder text
 * @param {Boolean|String} [props.resize] - Allows a multiline input to be resized
 * @param {Boolean} [props.required] - Sets component as required, only visual
 * @param {String} [props.size] - Sets the size of the input
 * @param {String} [props.suggestion] - Allows for autosuggest functionality
 * @param {String} [props.type] - Attribute for the input type, default is text
 * @param {String|Number} [props.value] - Value for the input
 * @param {('sm' | 'md')} [props.visualSize] - Sets component size, passed via ref from the convenience component
 * @param {Object} ref - Forwarded ref to TextEntry
 * @returns {React.Component} TextEntry component
 */
const TextEntry = /*#__PURE__*/_react.default.forwardRef(function (_ref, ref) {
  let {
      className,
      defaultValue: defaultProp = '',
      disabled,
      id,
      invalid,
      label,
      multiline,
      onBlur,
      onChange: onControllableChange = Function.prototype,
      onFocus,
      placeholder,
      resize,
      required,
      size,
      suggestion,
      type = 'text',
      value: controllableProp,
      visualSize
    } = _ref,
    rest = (0, _objectWithoutProperties2.default)(_ref, _excluded);
  // style is explicitly omitted, do not override.
  delete rest.style;
  const [isFocused, setFocused] = (0, _react.useState)(false);
  const [value, onChange] = (0, _useControllableState.default)({
    prop: controllableProp,
    defaultProp,
    onChange: onControllableChange
  });

  // Determine if the label should move
  // eslint-disable-next-line max-len
  const _onFocus = (0, _react.useCallback)(ev => {
    setFocused(true);
    typeof onFocus === 'function' && onFocus(ev);
  }, [setFocused, onFocus]);
  const _onBlur = (0, _react.useCallback)(ev => {
    setFocused(false);
    typeof onBlur === 'function' && onBlur(ev);
  }, [setFocused, onBlur]);
  const _onChange = (0, _react.useCallback)(ev => {
    onChange(ev.target.value, {
      event: ev
    });
  }, [onChange]);
  const TagName = multiline ? 'textarea' : 'input';
  const classNames = (0, _classnames.default)(`${UX_TEXT_ENTRY_CLASSNAME}-shell`, `${UX_TEXT_ENTRY_CLASSNAME}--size-${visualSize}`, className);
  return /*#__PURE__*/_react.default.createElement(_text.default.div, (0, _extends2.default)({
    as: "input",
    emphasis: invalid && 'critical',
    "data-version": "2400.6",
    className: classNames,
    size: textSize[visualSize]
  }, getShellProps({
    multiline,
    label,
    size,
    resize
  })), suggestion && /*#__PURE__*/_react.default.createElement("span", {
    className: `${UX_TEXT_ENTRY_CLASSNAME}-suggestion`
  }, suggestion), !placeholder && (0, _label.ensureLabelComponent)(label, {
    float: Boolean(value) || isFocused,
    internal: true,
    required,
    htmlFor: id
  }), /*#__PURE__*/_react.default.createElement(TagName, (0, _extends2.default)({
    ref: ref,
    className: `${UX_TEXT_ENTRY_CLASSNAME}-field`,
    disabled: disabled,
    id: id,
    placeholder: placeholder,
    "aria-invalid": invalid,
    "aria-required": Boolean(required),
    type: type,
    value: value,
    onChange: _onChange,
    onFocus: _onFocus,
    onBlur: _onBlur,
    size: size
  }, rest)));
});
TextEntry.propTypes = {
  /** Classes applied to the shell for positioning purposes */
  className: _propTypes.default.string,
  /** Provides default value */
  defaultValue: _propTypes.default.string,
  /** Sets the disabled state of the input */
  disabled: _propTypes.default.bool,
  /** Id applied to the input, used for the for attribute on the label */
  id: _propTypes.default.string.isRequired,
  /** Set component when input is invalid */
  invalid: _propTypes.default.bool,
  /** Optional label to float above input, placeholder will not show when using this */
  label: _propTypes.default.node,
  /** Uses a `<textarea/>` instead */
  multiline: _propTypes.default.bool,
  /** Blur handler for a input */
  onBlur: _propTypes.default.func,
  /** Change handler for a input */
  onChange: _propTypes.default.func,
  /** Focus handler for a input */
  onFocus: _propTypes.default.func,
  /** Placeholder for the input, do not supply with a floating label */
  placeholder: _propTypes.default.string,
  /** Allows a multiline input to be resized */
  resize: _propTypes.default.oneOfType([_propTypes.default.bool, _propTypes.default.oneOf(['horizontal', 'vertical'])]),
  /** Add asterisk to supplied label, aria-required */
  required: _label.default.propTypes.required,
  /** Set the size of the input */
  size: _propTypes.default.number,
  /** Text to suggest as completion of the input */
  suggestion: _propTypes.default.string,
  /** Input type */
  type: _propTypes.default.oneOf(inputTypes),
  /** Value for the input */
  value: _propTypes.default.node,
  /** Component size, passed via ref from the convenience component */
  visualSize: _propTypes.default.oneOf(['sm', 'md'])
};
TextEntry.displayName = 'TextEntry';
var _default = exports.default = TextEntry;